import { Controller } from '@hotwired/stimulus';
import { installWorkers, loaded, themeName, watchTheme } from '../lib/monaco';

const NARROW = '(max-width: 767px)';

const SCRIPTED = ['shell', 'ruby'];

let taught = false;

const OPTIONS = {
  automaticLayout: true,
  fontFamily: 'SF Mono, Menlo, monospace',
  fontSize: 13,
  minimap: { enabled: false },
  overviewRulerLanes: 0,
  padding: { top: 12 },
  quickSuggestions: { comments: false, other: true, strings: true },
  renderLineHighlight: 'gutter',
  scrollBeyondLastLine: false,
};

export default class extends Controller {
  static values = { workers: Object, variables: Array, readOnly: Boolean };

  initialize() {
    this.models = new Map();
    this.ready = this.#setup();
  }

  disconnect() {
    this.ready.then(() => this.#teardown());
  }

  async open(path, content, language) {
    const monaco = await this.ready;

    this.editor.setModel(this.#model(monaco, path, content, language));
  }

  async refresh() {
    await this.ready;

    this.editor.layout();
  }

  forget(path) {
    this.models.get(path)?.dispose();
    this.models.delete(path);
  }

  get value() {
    return this.editor.getValue();
  }

  async #setup() {
    installWorkers(this.workersValue);

    const monaco = await loaded;

    this.host = document.createElement('div');
    this.host.className = 'h-full';
    this.element.replaceChildren(this.host);

    this.#teachVariables(monaco);

    this.narrow = window.matchMedia(NARROW);
    this.editor = monaco.editor.create(this.host, {
      ...OPTIONS,
      readOnly: this.readOnlyValue,
      theme: themeName(),
      wordWrap: this.#wordWrap(),
    });

    this.editor.onDidChangeModelContent(() => {
      this.dispatch('change', { detail: { content: this.editor.getValue() } });
    });

    this.wrapLines = () => this.editor.updateOptions({ wordWrap: this.#wordWrap() });
    this.narrow.addEventListener('change', this.wrapLines);
    this.observer = watchTheme(monaco);

    this.dispatch('ready');

    return monaco;
  }

  #teachVariables(monaco) {
    if (taught || this.variablesValue.length === 0) return;

    taught = true;

    const variables = this.variablesValue;

    SCRIPTED.forEach((language) => {
      monaco.languages.registerCompletionItemProvider(language, {
        triggerCharacters: ['$'],
        provideCompletionItems: (model, position) => {
          const word = model.getWordUntilPosition(position);

          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };

          return {
            suggestions: variables.map((variable) => ({
              label: variable.name,
              kind: monaco.languages.CompletionItemKind.Variable,
              detail: variable.detail,
              documentation: variable.detail,
              insertText: variable.name,
              range,
            })),
          };
        },
      });

      monaco.languages.registerHoverProvider(language, {
        provideHover: (model, position) => {
          const word = model.getWordAtPosition(position);
          const found = variables.find((variable) => variable.name === word?.word);

          if (!found) return null;

          return { contents: [{ value: `**${found.name}**` }, { value: found.detail }] };
        },
      });
    });
  }

  #wordWrap() {
    return this.narrow.matches ? 'on' : 'off';
  }

  #teardown() {
    this.narrow.removeEventListener('change', this.wrapLines);
    this.observer.disconnect();
    this.editor.dispose();
    this.models.forEach((model) => model.dispose());
    this.models.clear();
  }

  #model(monaco, path, content, language) {
    if (!this.models.has(path)) this.models.set(path, monaco.editor.createModel(content, language));

    return this.models.get(path);
  }
}
